import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../../../context/ThemeContext";
import { getThemeClasses } from "../../../../utils/ThemeClasses";
import { useDebounce } from "../../../../hooks/useDebounce";
import PaginationControls from "../../../../components/base/PaginationControls";
import SgTable from "../../components/SgTable";
import type { SgColumn } from "../../components/SgTable";
import {
  fetchSgDevices,
  fetchAllSgDevices,
} from "../../../../features/sg/equipos";
import { useSgDeviceConnections } from "../../../../features/sg/useSgDeviceConnections";
import { fetchLastPositions } from "../../../../features/sg/socket";
import { timeAgo, connStatus } from "../../../../features/sg/format";
import { downloadXlsx } from "../../../../features/sg/xlsx";
import { deviceRow, fileDate } from "../../../../features/sg/exportRows";
import { notifyOk, notifyError } from "../../../../features/sg/notify";
import { StatusBadge, UserCell, ConnCell, OriginChips } from "../../components/cells";
import { useSgOrigins, fetchOrigins, originsOfImei } from "../../../../features/sg/origins";

const PER_PAGE = 30;

export default function DevicesList() {
  const navigate = useNavigate();
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const debounced = useDebounce(search, 400);

  async function handleExport() {
    setExporting(true);
    try {
      const all = await fetchAllSgDevices(debounced);
      const allImeis = all.map((d) => d.imei).filter(Boolean);
      const [posMap, originMap] = await Promise.all([
        fetchLastPositions(allImeis, { requestId: "tabla" }),
        fetchOrigins(allImeis).catch((e) => {
          notifyError(e.message);
          return {};
        }),
      ]);
      const headers = [
        t("devices.cols.name"),
        t("devices.cols.uniqId"),
        t("devices.cols.user"),
        t("barrido.cols.brand"),
        t("barrido.cols.model"),
        t("devices.cols.lastConn"),
        t("devices.cols.phone"),
        t("devices.cols.status"),
        t("devices.cols.tag"),
        t("barrido.cols.provider"),
        t("barrido.cols.platforms"),
        "Latitud",
        "Longitud",
        "Mapa",
      ];
      const xlsxRows = all.map((d) => deviceRow(d, posMap.get(d.imei), originMap));
      await downloadXlsx(`equipos_${fileDate()}.xlsx`, "Equipos", headers, xlsxRows);
      notifyOk(
        t("devices.exported", { n: xlsxRows.length.toLocaleString("es-MX") })
      );
    } catch (err: any) {
      notifyError(err.message);
    } finally {
      setExporting(false);
    }
  }

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["sg-devices", page, debounced],
    queryFn: () => fetchSgDevices(page, debounced, PER_PAGE),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const rows = data?.rows ?? [];

  const imeis = useMemo(
    () => rows.map((d: any) => d.imei).filter(Boolean),
    [rows]
  );
  const livePositions = useSgDeviceConnections(imeis);
  const origins = useSgOrigins(imeis);

  const columns: SgColumn[] = [
    {
      header: t("devices.cols.name"),
      key: "name",
      render: (d) => <span>{d.name || "—"}</span>,
    },
    {
      header: t("devices.cols.uniqId"),
      key: "uniq_id",
      render: (d) => <span>{d.uniq_id || "—"}</span>,
    },
    {
      header: t("devices.cols.user"),
      key: "user",
      render: (d) => (
        <UserCell name={d.users?.user_name || "—"} isDarkMode={isDarkMode} />
      ),
    },
    {
      header: t("barrido.cols.platforms"),
      key: "platforms",
      render: (d) => <OriginChips origins={originsOfImei(origins, d.imei)} />,
    },
    {
      header: t("devices.cols.model"),
      key: "model",
      render: (d) => (
        <span>
          {d.brands
            ? [d.brands.brand, d.brands.model].filter(Boolean).join(" - ")
            : "—"}
        </span>
      ),
    },
    {
      header: t("devices.cols.lastConn"),
      key: "lastConn",
      render: (d) => {
        const live = livePositions[d.imei];
        const lastConn = live?.timestamp || d.last_time_connected;
        return lastConn ? (
          <ConnCell
            status={connStatus(lastConn)}
            label={timeAgo(lastConn)}
            live={!!live}
          />
        ) : (
          <ConnCell status={null} label={t("devices.noData")} />
        );
      },
    },
    {
      header: t("devices.cols.phone"),
      key: "phone",
      render: (d) => <span>{d.phone || "—"}</span>,
    },
    {
      header: t("devices.cols.status"),
      key: "status",
      align: "center",
      render: (d) => (
        <StatusBadge
          label={d.statuses?.name_es || d.statuses?.name || "—"}
          isDarkMode={isDarkMode}
        />
      ),
    },
    {
      header: t("devices.cols.tag"),
      key: "etiqueta",
      render: (d) => <span>{d.device_conditions?.name || "—"}</span>,
    },
  ];

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <div
        className={`px-6 pt-6 pb-5 border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              {t("devices.title")}
            </h1>
            <p className={`text-sm mt-1 ${themeClasses.textSecondary}`}>
              {t("devices.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className={`text-sm px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors whitespace-nowrap border ${
                isDarkMode
                  ? "border-dark-border text-gray-200 hover:bg-white/5"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
              } disabled:opacity-60`}
            >
              <i className="ri-download-2-line"></i>
              <span>{exporting ? t("common.exporting") : t("common.export")}</span>
            </button>
            <button
              onClick={() => navigate("/sg/devices/form")}
              className="bg-primary-500 hover:bg-primary-400 text-white text-sm px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span>{t("devices.add")}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <i
              className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t("devices.searchPlaceholder")}
              className={`w-full pl-10 pr-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                isDarkMode
                  ? "bg-dark-card border-dark-border text-white placeholder-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              }`}
            />
          </div>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("devices.count", { n: total.toLocaleString("es-MX") })}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {isError ? (
          <p className="text-red-500 text-sm">
            {(error as Error)?.message || t("devices.loadError")}
          </p>
        ) : (
          <>
            <SgTable
              columns={columns}
              rows={rows}
              isDarkMode={isDarkMode}
              loading={isPending}
              emptyText={t("devices.empty")}
              rowKey={(d) => String(d.id)}
              onRowClick={(d) => navigate(`/sg/devices/edit/${d.id}`)}
            />
            {totalPages > 1 && (
              <PaginationControls
                currentPage={page + 1}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p - 1)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
